import { useCallback } from 'react';
import { getAudioService } from '@/shared/services/AudioService';
import type { SFXType } from '@/shared/services/AudioService';

/**
 * Hook for handling button clicks with sound effects
 *
 * Provides a memoized click handler that plays a sound effect before
 * invoking the original onClick callback. Respects the disabled state
 * to prevent sound/action when button is disabled.
 *
 * @param onClick - Original click handler from props
 * @param disabled - Whether the button is disabled
 * @param sound - Sound effect to play (defaults to 'pose')
 * @returns Memoized click handler with sound effect
 *
 * @example
 * ```tsx
 * function MyButton({ onClick, disabled }: Props) {
 *   const handleClick = useClickWithSound(onClick, disabled);
 *   return <button onClick={handleClick} disabled={disabled}>Click me</button>;
 * }
 * ```
 */
export function useClickWithSound(
  onClick: ((e: React.MouseEvent<HTMLButtonElement>) => void) | undefined,
  disabled: boolean | undefined,
  sound: SFXType = 'pose'
) {
  return useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!disabled) {
        getAudioService().playSFX(sound);
        onClick?.(e);
      }
    },
    [onClick, disabled, sound]
  );
}
